import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Mail,
  Phone,
  MapPin,
  Send,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const countries = [
  "South Africa",
  "Botswana",
  "Namibia",
  "Zimbabwe",
  "Mozambique",
  "Zambia",
  "Malawi",
  "Tanzania",
  "Kenya",
  "Uganda",
  "Other"
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const isDemo = searchParams.get('demo') === 'true';

  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    email: '',
    phone: '',
    country: '',
    message: '',
    is_demo_request: isDemo
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      is_demo_request: isDemo,
      message: isDemo ? "I'm interested in scheduling a demo of Certisfy for our organization." : ''
    }));
  }, [isDemo]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCountryChange = (value) => {
    setFormData(prev => ({ ...prev, country: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedToTerms) {
      toast.error('Please agree to the privacy policy to continue');
      return;
    }

    setIsSubmitting(true);

    try {
      const endpoint = formData.is_demo_request ? '/api/demo-request' : '/api/contact';
      await axios.post(`${BACKEND_URL}${endpoint}`, formData);
      
      setIsSubmitted(true);
      toast.success(
        formData.is_demo_request 
          ? 'Demo request submitted! We\'ll be in touch soon.' 
          : 'Message sent! We\'ll get back to you shortly.'
      );
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to submit form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="contact-page" className="min-h-[80vh] flex items-center justify-center bg-slate-50">
        <div className="container-custom">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 rounded-full bg-[#F0FDF4] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-[#1F7A3A]" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              {isDemo ? 'Demo Request Received!' : 'Message Sent!'}
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              {isDemo 
                ? 'Thank you for your interest in Certisfy. Our team will contact you within 1-2 business days to schedule your demo.'
                : 'Thank you for reaching out. We\'ll get back to you as soon as possible.'}
            </p>
            <Button 
              onClick={() => setIsSubmitted(false)}
              variant="outline"
              className="btn-secondary"
            >
              Send another message
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="section-padding bg-slate-50 leaf-pattern">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="badge-green mb-4 inline-block">
              {isDemo ? 'Request a Demo' : 'Contact Us'}
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              {isDemo 
                ? 'See Certisfy in action'
                : 'Get in touch'}
            </h1>
            <p className="text-lg text-slate-600">
              {isDemo 
                ? 'Schedule a personalized demonstration of the Certisfy platform with our team.'
                : 'Have questions about Certisfy? We\'re here to help.'}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#1F7A3A]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Email</p>
                    <a href="mailto:info@certisfy.cloud" className="text-slate-600 hover:text-[#1F7A3A]">
                      info@certisfy.cloud
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#1F7A3A]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Phone</p>
                    <a href="tel:+27713360548" className="text-slate-600 hover:text-[#1F7A3A]">
                      +27 (0) 71 336 0548
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#1F7A3A]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 mb-1">Office</p>
                    <p className="text-slate-600">
                      Pretoria, South Africa<br />
                      (Address details coming soon)
                    </p>
                  </div>
                </div>
              </div>

              {/* Demo Benefits */}
              {isDemo && (
                <div className="mt-8 p-6 bg-[#F0FDF4] rounded-2xl">
                  <h3 className="font-bold text-slate-900 mb-4">What to expect</h3>
                  <ul className="space-y-3 text-sm text-slate-600">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#1F7A3A] mt-0.5" />
                      30-45 minute personalized demo
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#1F7A3A] mt-0.5" />
                      Tailored to your organization's needs
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#1F7A3A] mt-0.5" />
                      Q&A with our product team
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#1F7A3A] mt-0.5" />
                      Implementation roadmap discussion
                    </li>
                  </ul>
                </div>
              )}
            </div>

            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-card">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="Your full name"
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="organization">Organization *</Label>
                      <Input
                        id="organization"
                        name="organization"
                        value={formData.organization}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="Company or organization"
                        data-testid="contact-organization"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="you@company.com"
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="mt-2"
                        placeholder="+27 71 336 0548"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="country">Country *</Label>
                    <Select onValueChange={handleCountryChange} value={formData.country}>
                      <SelectTrigger className="mt-2" data-testid="contact-country">
                        <SelectValue placeholder="Select your country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="mt-2"
                      placeholder={isDemo 
                        ? "Tell us about your organization and what you'd like to see in the demo..."
                        : "How can we help you?"}
                      data-testid="contact-message"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="terms"
                      checked={agreedToTerms}
                      onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                      data-testid="contact-terms"
                      className="mt-1"
                    />
                    <label htmlFor="terms" className="text-sm text-slate-600 font-normal cursor-pointer">
                      I agree to the <a href="/privacy" className="text-[#1F7A3A] hover:underline">Privacy Policy</a> and 
                      consent to being contacted about Certisfy products and services.
                    </label>
                  </div>

                  <Button 
                    type="submit" 
                    className="btn-primary w-full"
                    disabled={isSubmitting}
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {isDemo ? 'Request Demo' : 'Send Message'}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
